import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './PublicReclamationForm.css';

const PublicReclamationForm = () => {
  const { qrToken: paramToken } = useParams();
  const [searchParams] = useSearchParams();
  const qrToken = paramToken || searchParams.get('qr') || searchParams.get('token');

  const [loadingInfo, setLoadingInfo] = useState(true);
  const [equipmentInfo, setEquipmentInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Form states
  const [type, setType] = useState('reclamation');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Default fallback categories
  const defaultReclamations = [
    'Panne matérielle / Hors service',
    'Bouton ou commande défectueux',
    'Bruit ou vibration anormale',
    'Coussin / Assise endommagé',
    'Problème d\'hygiène / Propreté',
    'Autre dysfonctionnement'
  ];

  const defaultSuggestions = [
    'Amélioration du confort',
    'Ajout d\'équipements',
    'Service & Accueil',
    'Autre suggestion'
  ];

  const [customReclamationCategories, setCustomReclamationCategories] = useState(defaultReclamations);
  const [customSuggestionCategories, setCustomSuggestionCategories] = useState(defaultSuggestions);

  useEffect(() => {
    if (qrToken) {
      fetchEquipmentInfo(qrToken);
    } else {
      setLoadingInfo(false);
    }
  }, [qrToken]);

  const fetchEquipmentInfo = async (token) => {
    try {
      setLoadingInfo(true);
      const response = await axios.get(`/api/qr-equipments/public/${token}`);
      if (response.data.success) {
        const eq = response.data.equipment;
        setEquipmentInfo(eq);

        const locId = eq?.location?._id || eq?.location;
        if (locId) {
          try {
            const catRes = await axios.get(`/api/qr-categories/location/${locId}`);
            if (catRes.data.success && catRes.data.categories.length > 0) {
              const recCats = catRes.data.categories.filter(c => c.type === 'reclamation').map(c => c.name);
              const sugCats = catRes.data.categories.filter(c => c.type === 'suggestion').map(c => c.name);
              if (recCats.length > 0) {
                setCustomReclamationCategories(recCats);
                setCategory(recCats[0]);
              }
              if (sugCats.length > 0) {
                setCustomSuggestionCategories(sugCats);
              }
            } else {
              setCategory(defaultReclamations[0]);
            }
          } catch (cErr) {
            console.error('Erreur chargement motifs:', cErr);
            setCategory(defaultReclamations[0]);
          }
        }
      } else {
        setErrorMsg('Équipement non trouvé.');
      }
    } catch (err) {
      setErrorMsg('Erreur de chargement.');
    } finally {
      setLoadingInfo(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) {
      alert('Veuillez sélectionner un motif.');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      if (qrToken) formData.append('qrToken', qrToken);
      if (equipmentInfo?.location?._id) formData.append('locationId', equipmentInfo.location._id);
      if (equipmentInfo?._id) formData.append('equipmentId', equipmentInfo._id);

      formData.append('type', type);
      formData.append('category', category);
      formData.append('description', description);
      formData.append('clientName', clientName);
      formData.append('clientPhone', clientPhone);

      if (photoFile) {
        formData.append('photo', photoFile);
      }

      const response = await axios.post('/api/reclamations/public', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setSubmitted(true);
      } else {
        alert(response.data.message || 'Erreur lors de l\'envoi.');
      }
    } catch (err) {
      alert('Erreur lors de l\'envoi de votre signalement.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInfo) {
    return (
      <div className="rec-public-container">
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 text-muted small">Chargement...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="rec-public-container">
        <div className="rec-success-card">
          <div className="fs-1 mb-2">🎉</div>
          <h2>Signalement Transmis !</h2>
          <p className="mt-2">Notre équipe s'en occupe immédiatement.</p>
          <button 
            className="rec-submit-main mt-4" 
            onClick={() => {
              setSubmitted(false);
              setDescription('');
              setPhotoFile(null);
              setPhotoPreview(null);
            }}
          >
            + Nouveau Signalement
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rec-public-container">
      <div className="rec-form-card">
        {/* Header Ultra-Compact */}
        <div className="rec-header">
          <h1>Assistance Client</h1>
          {equipmentInfo && (
            <div className="rec-compact-banner">
              <span>📍 {equipmentInfo.location?.name || 'Site'}</span>
              <span className="dot">•</span>
              <span>🛋️ {equipmentInfo.name}</span>
            </div>
          )}
          {errorMsg && <div className="text-danger small mt-1">{errorMsg}</div>}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Commutateur Panne / Suggestion */}
          <div className="rec-segmented-control">
            <button
              type="button"
              className={`rec-seg-btn ${type === 'reclamation' ? 'active-danger' : ''}`}
              onClick={() => {
                setType('reclamation');
                setCategory(customReclamationCategories[0] || '');
              }}
            >
              ⚠️ Panne
            </button>
            <button
              type="button"
              className={`rec-seg-btn ${type === 'suggestion' ? 'active-primary' : ''}`}
              onClick={() => {
                setType('suggestion');
                setCategory(customSuggestionCategories[0] || '');
              }}
            >
              💡 Suggestion
            </button>
          </div>

          {/* Motif Select */}
          <div className="rec-input-group">
            <label>Motif *</label>
            <select
              className="rec-input-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              {(type === 'reclamation' ? customReclamationCategories : customSuggestionCategories).map((cat, idx) => (
                <option key={idx} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Photo Capture */}
          <div className="rec-input-group">
            <label>Photo (optionnel)</label>
            {photoPreview ? (
              <div className="rec-preview-card">
                <img src={photoPreview} alt="Panne" />
                <div className="rec-preview-actions">
                  <label htmlFor="rec-camera-input" className="rec-action-btn">🔄 Changer</label>
                  <button type="button" className="rec-action-btn delete" onClick={handleRemovePhoto}>✖</button>
                </div>
              </div>
            ) : (
              <label htmlFor="rec-camera-input" className="rec-photo-box">
                <span className="rec-photo-icon">📷</span>
                <span className="rec-photo-label">Prendre une photo</span>
              </label>
            )}
            <input
              id="rec-camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="d-none"
              onChange={handlePhotoChange}
            />
          </div>

          {/* Commentaire */}
          <div className="rec-input-group">
            <label>Commentaire</label>
            <textarea
              className="rec-input-text"
              rows="2"
              placeholder="Décrivez brièvement la panne..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            ></textarea>
          </div>

          {/* Contact Optionnel Compact */}
          <div className="rec-contact-row">
            <input
              type="text"
              className="rec-input-sm"
              placeholder="Nom (optionnel)"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
            <input
              type="tel"
              className="rec-input-sm"
              placeholder="Tél (optionnel)"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
            />
          </div>

          {/* Action Button */}
          <button type="submit" className="rec-submit-main" disabled={submitting}>
            {submitting ? 'Envoi...' : 'Envoyer 🚀'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicReclamationForm;
